import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { useMediaQuery } from 'react-responsive';
import ScrollAnimation from 'react-animate-on-scroll';
import Slider from 'react-slick';
import { Container, Row, Column, Carousel, Icon } from '@pella/sash-framework';
import { Layout, SEO, Breadcrumbs, Image, Video, CTA } from '../../components';
import { isValidArray } from '../../components/utilities/validations';
import ResultsTile from '../plp/results/tile';
import ImageWithClickPoints from '../../components/common/image-with-click-points';
import RTAShowroomPanel from '../../components/template/homepage/rta-showroom-panel';
import styles from './index.module.scss';

const ESOTemplate = (props) => {
	const [fullVideo, setFullVideo] = useState({});
	const pageData = props.data.contentstackEso;
	const isMobile = useMediaQuery({ query: '(max-width: 768px)' });
	const stripPTag = (string) => string.replace(/(<p[^>]+?>|<p>|<\/p>)/gim, '');
	const isBlackBg = pageData.black_background;

	const onFullVideoPlay = (index) => {
		const vid = document.getElementById(`fullVideo${index}`);

		if (fullVideo[`fullVideo${index}`]) {
			vid.pause();
			setFullVideo({ ...fullVideo, [`fullVideo${index}`]: false });
		} else {
			vid.play();
			setFullVideo({ ...fullVideo, [`fullVideo${index}`]: true });
		}
	};

	useEffect(() => {
		if (pageData?.content_blocks?.[0] && pageData.content_blocks.find((block) => block.feature != null)) {
			let videos = {};
			for (let i = 0; i < pageData.content_blocks.length; i++) {
				if (pageData.content_blocks[i].feature != null && (pageData.content_blocks[i].feature.internal_video?.url || pageData.content_blocks[i].feature.video?.[0])) {
					videos[`fullVideo${i}`] = pageData.content_blocks[i].feature.autoplay;
				}
			}
			setFullVideo(videos);
		}
	}, [pageData]);

	return (
		<Layout
			id='template-ESOTemplate'
			className={`${styles.esoLayout} ${isBlackBg ? 'bg-black' : ''}`}
		>
			{pageData.breadcrumb && (
				<Breadcrumbs
					data={pageData.breadcrumb}
					currentUrl={pageData.url}
					className={styles.esoBreadcrumbs}
				/>
			)}

			{pageData.hero && (
				<div className={`esoHero relative overflow-hidden bg-black before:absolute before:bottom-0 before:left-0 before:z-9 before:h-2/5 before:w-full before:bg-gradient-to-t before:from-black before:content-[""] ${pageData.hero.hero_height === 'half' ? 'h-[50vh]' : 'h-[calc(90vh-159px)] md:h-[calc(100vh-63px)] 2xl:h-[calc(100vh-98px)]'}`}>
					{pageData.hero.internal_video?.url && (
						<video
							autoPlay={true}
							muted={true}
							loop={true}
							poster={pageData.hero.hero_image[0].image.url + '?format=pjpg&auto=webp'}
							className='esoHero-video absolute left-1/2 top-1/2 min-h-full min-w-full max-w-none -translate-x-1/2 -translate-y-1/2 max-md:portrait:h-full max-lg:landscape:w-full'
						>
							<source
								src={pageData.hero.internal_video.url}
								type='video/mp4'
							/>
							<Image
								data={pageData.hero.hero_image[0]}
								className='esoHero-videoPoster absolute left-0 top-0 !block h-full w-full'
							/>
						</video>
					)}

					{!pageData.hero.internal_video?.url && pageData.hero.hero_image?.[0] && (
						<Image
							className='esoHero-image h-full'
							data={pageData.hero.hero_image[0]}
							loading='eager'
						/>
					)}

					{pageData.hero.heading && pageData.hero.text_overlay && (
						<h1
							dangerouslySetInnerHTML={{ __html: stripPTag(pageData.hero.heading) }}
							className='esoHero-heading absolute bottom-[30px] left-0 z-10 m-0 w-full px-5 text-center text-[calc(1.5rem+3.3vw)] font-light !leading-tight text-white 2xl:bottom-[100px] 2xl:text-[4.5rem]'
						/>
					)}
				</div>
			)}

			{!pageData.hero?.text_overlay && pageData.hero?.heading && (
				<Container width={1800}>
					<ScrollAnimation
						animateIn='fadeIn'
						animateOnce={true}
						duration={1.5}
					>
						<h1
							dangerouslySetInnerHTML={{ __html: stripPTag(pageData.hero.heading) }}
							className={`esoHero-Heading mb-0 pt-5 text-center text-[calc(1.5rem+3.3vw)] font-light lg:pb-8 lg:pt-12 2xl:text-[3.5rem] ${isBlackBg ? 'text-white' : ''}`}
						/>
					</ScrollAnimation>
				</Container>
			)}

			{pageData.content_blocks?.[0] &&
				pageData.content_blocks.map((block, index) => {
					if (block.feature != null) {
						const isAfterTextBlock = pageData.content_blocks[index - 1]?.text_block != null && !block.feature.heading ? true : false;

						return (
							<div
								key={index}
								className={`${styles.esoFeature} ${block.feature.black_background ? styles.esoFeature_bgBlack : ''} ${isAfterTextBlock ? '!pt-0' : ''}`}
								style={block.feature.file_background?.url ? { backgroundImage: `url('${block.feature.file_background.url}')` } : null}
							>
								<Container
									width={1920}
									noPadding={true}
								>
									<Row
										noGutters={true}
										reverse={block.feature.text_position === 'left' ? true : false}
										align='center'
										justify={block.feature.video_position === 'center' ? 'center' : ''}
									>
										<Column
											xs={12}
											lg={7}
										>
											{!block.feature.internal_video && block.feature.image?.[0] && (
												<ScrollAnimation
													animateIn={isMobile || block.feature.video_position === 'center' ? 'fadeIn' : block.feature.text_position === 'left' ? 'fadeInRight' : 'fadeInLeft'}
													animateOnce={true}
													duration={1.5}
													offset={isMobile ? 200 : 0}
												>
													{block.feature.image.length < 2 && (
														<Image
															data={block.feature.image[0]}
															className={`${styles.esoFeature_Image} ${block.feature.text_position === 'left' ? styles.esoFeature_Image_right : ''}`}
														/>
													)}
													{block.feature.image.length > 2 && (
														<Carousel
															showArrows
															showDots
														>
															{block.feature.image.map((img, index) => (
																<Image
																	data={img}
																	key={index}
																	className={styles.esoFeature_Image}
																/>
															))}
														</Carousel>
													)}
												</ScrollAnimation>
											)}

											{block.feature.video?.[0] && (
												<ScrollAnimation
													animateIn={isMobile || block.feature.video_position === 'center' ? 'fadeIn' : block.feature.text_position === 'left' ? 'fadeInRight' : 'fadeInLeft'}
													animateOnce={true}
													duration={1.5}
													offset={isMobile ? 200 : 0}
												>
													<Video
														data={block.feature.video[0]}
														autoPlay={block.feature.autoplay}
													/>
												</ScrollAnimation>
											)}

											{block.feature.internal_video?.url && (
												<ScrollAnimation
													animateIn={isMobile || block.feature.video_position === 'center' ? 'fadeIn' : block.feature.text_position === 'left' ? 'fadeInRight' : 'fadeInLeft'}
													animateOnce={true}
													duration={1.5}
													offset={isMobile ? 200 : 0}
												>
													<button
														className={`${styles.esoFullFeature_PlayButton} ${fullVideo[`fullVideo${index}`] ? styles.esoFullFeature_PlayButton_active : ''}`}
														onClick={() => onFullVideoPlay(index)}
														title={fullVideo[`fullVideo${index}`] ? 'Pause Video' : 'Play Video'}
													>
														<span className={styles.esoFullFeature_ReaderOnly}>{fullVideo[`fullVideo${index}`] ? 'Pause Video' : 'Play Video'}</span>
														<div className={styles.esoFullFeature_VideoIcon}>
															<Icon
																fill={1}
																size={60}
																name={!fullVideo[`fullVideo${index}`] ? 'play_arrow' : 'pause'}
															/>
														</div>
													</button>

													<Video
														id={`fullVideo${index}`}
														data={{
															video: {
																video_id: block.feature.internal_video.url,
															},
															transcript: null,
															schema: null,
															autoplay: block.feature.autoplay,
															image_overlay: block.feature.image,
														}}
														youtube={false}
													/>
												</ScrollAnimation>
											)}
										</Column>

										{(block.feature.heading || block.feature.description) && (
											<Column
												xs={12}
												lg={5}
											>
												<div className={`${styles.esoFeature_Content}`}>
													{block.feature.heading && (
														<ScrollAnimation
															animateIn={block.feature.text_position === 'left' ? 'fadeInLeft' : 'fadeInRight'}
															animateOnce={true}
															duration={1.5}
															offset={isMobile ? 0 : null}
														>
															<h2
																className={styles.esoFeature_Heading}
																dangerouslySetInnerHTML={{ __html: block.feature.heading }}
															/>
														</ScrollAnimation>
													)}

													{block.feature.description && (
														<ScrollAnimation
															animateIn={block.feature.text_position === 'left' ? 'fadeInLeft' : 'fadeInRight'}
															animateOnce={true}
															duration={1.5}
															delay={isMobile ? 0 : 200}
															offset={0}
														>
															<div dangerouslySetInnerHTML={{ __html: block.feature.description }} />
														</ScrollAnimation>
													)}
												</div>
											</Column>
										)}
									</Row>
								</Container>
							</div>
						);
					}

					if (block.text_block != null && block.text_block.block_number !== 'carousel') {
						return (
							<div
								key={index}
								className={`${styles.esoTextBlock} ${block.text_block.justify === 'center' ? 'text-center' : ''} ${block.text_block.black_background ? styles.esoTextBlock_bgBlack : ''}`}
								style={block.text_block.background?.url ? { backgroundImage: `url('${block.text_block.background.url}')` } : null}
							>
								<Container width={1800}>
									<div className={styles.esoTextBlock_Intro}>
										{block.text_block.heading && (
											<ScrollAnimation
												animateIn='fadeInUp'
												animateOnce={true}
												duration={1.5}
												animatePreScroll={false}
											>
												<h2
													className={`${isBlackBg ? 'text-white' : ''} mb-5 text-center text-[calc(1rem+1.75vw)] 2xl:text-4xl`}
													dangerouslySetInnerHTML={{ __html: stripPTag(block.text_block.heading) }}
												/>
											</ScrollAnimation>
										)}

										{block.text_block.description && (
											<ScrollAnimation
												animateIn='fadeInUp'
												animateOnce={true}
												duration={1.5}
												delay={isMobile ? 100 : 300}
												animatePreScroll={false}
											>
												<div
													className={`${isBlackBg ? 'text-white' : ''} text-lg xl:text-2xl`}
													dangerouslySetInnerHTML={{ __html: block.text_block.description }}
												/>
											</ScrollAnimation>
										)}
									</div>
								</Container>

								<Container width={block.text_block.block_number > 2 ? 1800 : 1600}>
									{isValidArray(block?.text_block?.blocks) && (
										<Row
											verticalGutters={true}
											largeGutters={true}
											className={styles.esoTextBlock_Blocks}
										>
											{block.text_block.blocks.map((item, index) => (
												<Column
													xs={12}
													md={12 / block.text_block.block_number}
													key={index}
												>
													<ScrollAnimation
														animateIn='fadeInUp'
														animateOnce={true}
														duration={1.5}
														offset={isMobile ? 0 : null}
														delay={isMobile && index > 0 ? index * 350 : !isMobile && index > 0 ? index * 200 : 0}
													>
														{item.heading && (
															<h3
																dangerouslySetInnerHTML={{ __html: item.heading }}
																className={item.heading_placement === 'center' ? 'text-center' : item.heading_placement === 'right' ? 'text-right' : 'text-left'}
															/>
														)}
													</ScrollAnimation>

													<ScrollAnimation
														animateIn='fadeInUp'
														animateOnce={true}
														duration={1.5}
														offset={isMobile ? 0 : null}
														delay={isMobile && index > 0 ? index * 350 + 100 : !isMobile && index > 0 ? index * 200 + 100 : 100}
													>
														{item.description && <div dangerouslySetInnerHTML={{ __html: item.description }} />}
													</ScrollAnimation>
												</Column>
											))}
										</Row>
									)}
								</Container>
							</div>
						);
					}

					if (block.text_block != null && block.text_block.block_number === 'carousel') {
						return (
							<div
								key={index}
								className='esoTextBlock bg-cover bg-center bg-no-repeat py-8 md:py-12'
								style={block.text_block.background?.url ? { backgroundImage: `url('${block.text_block.background.url}')` } : null}
							>
								<Container width={1800}>
									<div className={styles.esoTextBlock_Intro}>
										{block.text_block.heading && (
											<ScrollAnimation
												animateIn='fadeInUp'
												animateOnce={true}
												duration={1.5}
												animatePreScroll={false}
											>
												<h2
													className={`${isBlackBg ? 'text-white' : ''} mb-5 text-center text-[calc(1rem+1.75vw)] 2xl:text-4xl`}
													dangerouslySetInnerHTML={{ __html: stripPTag(block.text_block.heading) }}
												/>
											</ScrollAnimation>
										)}

										{block.text_block.description && (
											<ScrollAnimation
												animateIn='fadeInUp'
												animateOnce={true}
												duration={1.5}
												delay={isMobile ? 100 : 300}
												animatePreScroll={false}
											>
												<div
													className={`${isBlackBg ? 'text-white' : ''} text-lg xl:text-2xl`}
													dangerouslySetInnerHTML={{ __html: block.text_block.description }}
												/>
											</ScrollAnimation>
										)}
									</div>
								</Container>

								<Container width={1800}>
									{block.text_block.blocks?.[0] && (
										<Slider
											slidesToShow={1}
											slidesToScroll={1}
											className='quotesSlider'
											autoplaySpeed={5000}
											arrows={false}
											dots
											lazyLoad
											autoplay
										>
											{block.text_block.blocks.map((item, index) => (
												<div key={index}>
													{item.heading && (
														<h3
															dangerouslySetInnerHTML={{ __html: item.heading }}
															className={isBlackBg ? 'text-white' : ''}
														/>
													)}

													{item.description && (
														<div
															dangerouslySetInnerHTML={{ __html: item.description }}
															className={isBlackBg ? '[&_p]:!text-white' : ''}
														/>
													)}
												</div>
											))}
										</Slider>
									)}
								</Container>
							</div>
						);
					}

					if (block.lead_gen != null) {
						return (
							<div
								key={index}
								className={`${styles.eso_leadGenCTA} ${block.lead_gen.background === 'dark' ? 'bg-black' : 'bg-white'}`}
							>
								<Row
									justify='center'
									align='center'
									noGutters={true}
								>
									<ScrollAnimation
										animateIn={'fadeInLeft'}
										animateOnce={true}
										duration={1.5}
										delay={isMobile ? 0 : 200}
										offset={0}
									>
										<Column
											className={styles.eso_ctaHeadingBot}
											xs={12}
											lg={12}
										>
											<h2 className={block.lead_gen.background === 'dark' ? 'text-white' : 'text-black'}>{block.lead_gen.heading}</h2>
											<div
												dangerouslySetInnerHTML={{
													__html: block.lead_gen.subheading,
												}}
												className={block.lead_gen.background === 'dark' ? 'text-white' : 'text-black'}
											/>
										</Column>
									</ScrollAnimation>

									<ScrollAnimation
										animateIn={'fadeInRight'}
										animateOnce={true}
										duration={1.5}
										delay={isMobile ? 0 : 200}
										offset={0}
									>
										<Column
											className={styles.eso_floatCTA}
											xs={12}
											sm={12}
											lg={12}
										>
											{block.lead_gen.ctas.map((ctaItem, index) => (
												<CTA
													key={index}
													className={styles.comp_floatCTA}
													color='primary'
													data={ctaItem}
												/>
											))}
										</Column>
									</ScrollAnimation>
								</Row>
							</div>
						);
					}

					if (block.full_feature != null) {
						return (
							<div
								key={index}
								className={`${styles.esoFullFeature} ${block.full_feature.internal_video?.url ? styles.esoFullFeature_Video : ''} ${block.full_feature.black_background ? styles.esoFullFeature_bgBlack : ''}`}
							>
								{block.full_feature.heading && (
									<ScrollAnimation
										animateIn='fadeInUp'
										animateOnce={true}
										offset={400}
									>
										<h2 className={styles.esoH2}>{block.full_feature.heading}</h2>
									</ScrollAnimation>
								)}

								{block.full_feature.subheading && (
									<ScrollAnimation
										animateIn='fadeInUp'
										animateOnce={true}
										offset={400}
										delay={isMobile ? 100 : 300}
									>
										<p className={styles.esoFullFeature_Intro}>{block.full_feature.subheading}</p>
									</ScrollAnimation>
								)}

								<ScrollAnimation
									animateIn='fadeIn'
									animateOnce={true}
									offset={400}
								>
									{!block.full_feature.video?.[0] && !block.full_feature.internal_video?.url && block.full_feature.banner_image?.[0] && (
										<Image
											data={block.full_feature.banner_image[0]}
											className={styles.esoFullFeature_Image}
										/>
									)}

									{block.full_feature.internal_video?.url && block.full_feature.banner_image?.[0] && (
										<>
											<button
												className={`${styles.esoFullFeature_PlayButton} ${fullVideo[`fullVideo${index}`] ? styles.esoFullFeature_PlayButton_active : ''}`}
												onClick={() => onFullVideoPlay(index)}
												title={fullVideo[`fullVideo${index}`] ? 'Pause Video' : 'Play Video'}
											>
												<span>{fullVideo[`fullVideo${index}`] ? 'Pause Video' : 'Play Video'}</span>
												<div className={styles.esoFullFeature_VideoIcon}>{!fullVideo[`fullVideo${index}`] ? <Icon name='play_arrow' /> : <Icon name='pause' />}</div>
											</button>

											<video
												autoPlay={true}
												loop={true}
												muted={true}
												id={`fullVideo${index}`}
												poster={block.full_feature.banner_image[0].image.url + '?format=pjpg&auto=webp'}
											>
												<source
													src={block.full_feature.internal_video.url}
													type='video/mp4'
												/>
											</video>
										</>
									)}
								</ScrollAnimation>
							</div>
						);
					}

					if (block.available != null && block.available.products?.[0]) {
						return (
							<div
								key={index}
								className='esoAvailable overflow-hidden bg-black py-8 xl:py-14'
								style={
									block.available.background_image?.url
										? {
												backgroundImage: `url('${block.available.background_image.url}')`,
											}
										: null
								}
							>
								<Container width={1800}>
									<ScrollAnimation
										animateIn='fadeInUp'
										animateOnce={true}
										offset={isMobile ? 0 : 200}
									>
										{block.available.heading && (
											<div
												className='esoAvailable-heading mb-6 text-center text-22px text-white md:text-28px xl:mb-12 xl:text-[38px]'
												dangerouslySetInnerHTML={{ __html: block.available.heading }}
											/>
										)}
									</ScrollAnimation>

									<div className='flex flex-row flex-wrap justify-center gap-y-10 md:-mx-[15px] xl:-mx-8 xl:gap-y-16'>
										{block.available.products.map((item, index) => (
											<div
												className='max-w-full flex-12/12 md:max-w-6/12 md:flex-6/12 md:px-[15px] lg:max-w-3/12 lg:flex-3/12 xl:px-8'
												key={index}
											>
												{item.tile?.[0] && (
													<ScrollAnimation
														animateIn='fadeInUp'
														animateOnce={true}
														delay={index * 250}
														offset={isMobile ? 0 : 200}
														className='relative h-full'
													>
														<ResultsTile
															tile={item.tile[0]}
															pdpURL={item.tile[0].static_url}
														/>
													</ScrollAnimation>
												)}

												{!item.tile?.[0] && (
													<ScrollAnimation
														animateIn='fadeInUp'
														animateOnce={true}
														delay={index * 250}
														offset={isMobile ? 0 : 200}
														className='relative text-center'
													>
														<CTA
															data={item.ctas[0]}
															className='absolute left-0 top-0 z-2 h-full w-full -indent-full'
															renderAs='link'
														>
															{item.product_line && <span>{item.product_line}</span>}
															{item.product_type && <span>{item.product_type}</span>}
														</CTA>

														{item.image && item.image[0] && (
															<div>
																<Image
																	data={item.image[0]}
																	className='mb-6 !block aspect-[3/2] w-full'
																/>
															</div>
														)}

														<div className='md:px-6 xl:px-8'>
															{item.product_line && <p className='mb-2 text-xs font-bold uppercase !leading-tight tracking-3px text-white'>{item.product_line}</p>}
															{item.product_type && <p className='m-0 text-base text-white'>{item.product_type}</p>}

															{item.ctas?.[0] && (
																<CTA
																	className='relative z-3 mt-4'
																	data={item.ctas[0]}
																	size='sm'
																	wide
																/>
															)}
														</div>
													</ScrollAnimation>
												)}
											</div>
										))}
									</div>
								</Container>
							</div>
						);
					}

					if (block.popout_image?.popout_image?.length > 0) {
						return (
							<div
								key={index}
								className='esoPopoutImage bg-cover bg-center bg-no-repeat py-8 md:py-12 xl:py-16'
								style={{ backgroundImage: `url('${block.popout_image.background_file?.url}')` }}
							>
								<Container>
									<ImageWithClickPoints data={block.popout_image.popout_image[0]} />
								</Container>
							</div>
						);
					}

					if (block.non_global_cards != null) {
						return (
							<div
								key={index}
								className='esoCards py-6 md:py-12 xl:py-16'
							>
								<Container width={1800}>
									{block.non_global_cards.heading && (
										<ScrollAnimation
											animateIn='fadeIn'
											animateOnce={true}
											offset={isMobile ? 0 : 200}
										>
											<h2 className={`mb-5 text-center ${isBlackBg ? 'text-white' : ''}`}>{block.non_global_cards.heading}</h2>
										</ScrollAnimation>
									)}

									{block.non_global_cards.card?.[0] && (
										<Carousel
											slidesToShow={{ xs: 1, md: 2, lg: 3, xl: 4 }}
											slidePadding={24}
											dotsClassName='[&_button]:before:bg-gray-50 [&_button[aria-current="true"]]:before:bg-white'
											showDots
										>
											{block.non_global_cards.card.map((card, index) => (
												<ScrollAnimation
													animateIn='fadeIn'
													animateOnce={true}
													offset={isMobile ? 0 : 200}
													delay={isMobile && index > 0 ? index * 350 : !isMobile && index > 0 ? index * 200 : 0}
													className='esoCard group h-full bg-white text-center'
													key={index}
												>
													{card.image_hover?.[0] && (
														<Image
															data={card.image_hover[0]}
															className={`aspect-[3/2] w-full object-cover ${card.image_animation?.[0] ? 'group-focus-within:!hidden group-hover:!hidden' : ''}`}
														/>
													)}

													{card.image_animation?.[0] && (
														<Image
															data={card.image_animation[0]}
															className='hidden aspect-[3/2] w-full object-cover group-focus-within:block group-hover:block'
														/>
													)}

													<div className='p-4 md:p-6'>
														{card.heading && <h3 className='text-xl tracking-[0.15px] md:text-22px xl:text-[23px]'>{card.heading}</h3>}
														{card.description && <div dangerouslySetInnerHTML={{ __html: card.description }} />}
													</div>
												</ScrollAnimation>
											))}
										</Carousel>
									)}
								</Container>
							</div>
						);
					}

					if (block.rta != null) {
						return (
							<RTAShowroomPanel
								key={index}
								data={block.rta}
								className='[&_h2]:max-md:max-w-[30ch] [&_h2]:xl:text-left'
							/>
						);
					}
				})}
		</Layout>
	);
};

ESOTemplate.propTypes = {
	data: PropTypes.object,
	location: PropTypes.object,
};

export default ESOTemplate;

// eslint-disable-next-line react/prop-types -- fix this
export const Head = ({ data }) => (
	<SEO
		// eslint-disable-next-line react/prop-types -- fix this
		{...data.contentstackEso.seo}
	/>
);

export const query = graphql`
	query ($uid: String!) {
		contentstackEso(id: { eq: $uid }) {
			url
			breadcrumb {
				active_page
				back_pages {
					...CTA
				}
			}
			black_background
			hero {
				internal_video {
					url
				}
				hero_image {
					...Image
					image {
						gatsbyImageData(width: 1920, layout: FULL_WIDTH, quality: 90)
					}
				}
				text_overlay
				heading
				hero_height
			}
			content_blocks {
				feature {
					black_background
					file_background {
						url
					}
					autoplay
					video_position
					video {
						...Video
					}
					internal_video {
						url
					}
					image {
						...Image
						image {
							gatsbyImageData(width: 1120, quality: 90)
						}
					}
					text_position
					heading
					description
				}
				text_block {
					justify
					black_background
					background {
						url
					}
					heading
					description
					block_number
					blocks {
						position
						heading
						description
					}
				}
				full_feature {
					black_background
					autoplay
					heading
					subheading
					video {
						...Video
					}
					internal_video {
						url
					}
					banner_image {
						...Image
						image {
							gatsbyImageData(width: 1920, layout: FULL_WIDTH, quality: 90)
						}
					}
				}
				lead_gen {
					background
					heading
					subheading
					ctas {
						...CTA
					}
				}
				available {
					background_image {
						url
					}
					heading
					products {
						image {
							...Image
							image {
								gatsbyImageData(height: 350, quality: 90)
							}
						}
						product_line
						product_type
						ctas {
							...CTA
						}
						tile {
							...Tile
						}
					}
				}
				popout_image {
					background_file {
						url
					}
					popout_image {
						...PopoutImage
					}
				}
				non_global_cards {
					heading
					card {
						image_hover {
							...Image
							image {
								url
								gatsbyImageData(quality: 90)
							}
						}
						image_animation {
							...Image
						}
						heading
						description
					}
				}
				rta {
					heading
				}
			}
			seo {
				title
				description
				image: thumbnail {
					url
				}
				noIndex: no_index_page
			}
		}
	}
`;
